import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { FileText, Heart, ArrowLeft, TrendingUp, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';

const UserDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [myReports, setMyReports] = useState([]);
  const [myDonations, setMyDonations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Attach token to requests
  const getAuthConfig = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, []);

  const extractList = (data) => {
    if (Array.isArray(data)) return data;
    return data?.data ?? data?.reports ?? [];
  };

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);

      const config = getAuthConfig();

      const [reportsRes, donationsRes] = await Promise.all([
        axios.get(`${API}/reports`, config),
        axios.get(`${API}/donations/user`, config),
      ]);

      const reports = extractList(reportsRes.data);
      const donations = extractList(donationsRes.data);

      // UUID-safe comparison (no Number())
      const userId = user?.id != null ? String(user.id) : '';

      const userReports = (reports || []).filter((r) => {
        const reportUserId =
          r.user_id ?? r.user?.id ?? r.created_by ?? r.createdBy ?? r.reporter_id ?? r.reporterId;
        return reportUserId != null && String(reportUserId) === userId;
      });

      // If API doesn't provide user id fields, fallback to show whatever it returned
      const hasAnyUserField = (reports || []).some(
        (r) =>
          r.user_id != null ||
          r.user?.id != null ||
          r.created_by != null ||
          r.createdBy != null ||
          r.reporter_id != null ||
          r.reporterId != null
      );

      setMyReports(hasAnyUserField ? userReports : (reports || []));
      setMyDonations(donations || []);
    } catch (err) {
      console.error('Failed to fetch user data', err);
      toast.error('Failed to load dashboard data. Please try again.');
      setMyReports([]);
      setMyDonations([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthConfig, user?.id, t]);

  // ✅ No eslint-disable; effect runs when user.id is ready
  useEffect(() => {
    if (!user?.id) return;
    fetchUserData();
  // eslint is fine because we depend only on user.id
  }, [user?.id,fetchUserData]);

  const downloadInvoice = async (donationId, e) => {
    e.stopPropagation();
    try {
      const response = await axios.get(`${API}/donations/${donationId}/invoice`, {
        ...getAuthConfig(),
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${donationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to download invoice');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getTotalDonated = () => {
    return (myDonations || [])
      .filter((d) => d.status === 'completed')
      .reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
  };

  const statusLabel = (s) => {
    if (!s) return '';
    return t(`status.${s}`, s);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <button
            data-testid="back-home-dashboard-btn"
            onClick={() => navigate('/')}
            className="flex items-center text-white hover:text-blue-100 mb-6"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t('nav.backHome')}
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl font-bold mb-2" data-testid="user-name">{t('dashboard.welcome')} {user?.name || 'User'}</h1>
              <p className="text-blue-100" data-testid="user-phone">{user?.phone || ''}</p>
              {user?.city && <p className="text-blue-100">{user.city}</p>}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-30">
              <FileText size={24} className="mb-2" />
              <div className="text-3xl font-bold" data-testid="my-reports-count">{myReports.length}</div>
              <div className="text-sm text-blue-100">{t('dashboard.myReports')}</div>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-30">
              <Heart size={24} className="mb-2" />
              <div className="text-3xl font-bold" data-testid="my-donations-count">{myDonations.length}</div>
              <div className="text-sm text-blue-100">{t('dashboard.donations')}</div>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-30">
              <TrendingUp size={24} className="mb-2" />
              <div className="text-3xl font-bold" data-testid="total-donated">₹{getTotalDonated().toLocaleString()}</div>
              <div className="text-sm text-blue-100">{t('dashboard.totalContributed')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Tabs defaultValue="reports" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="reports" data-testid="reports-tab">{t('dashboard.myReportsTab')}</TabsTrigger>
            <TabsTrigger value="donations" data-testid="donations-tab">{t('dashboard.myDonationsTab')}</TabsTrigger>
          </TabsList>
          <TabsContent value="reports" className="mt-8">
            {loading ? (
              <div className="text-center py-12">{t('common.loading')}</div>
            ) : myReports.length === 0 ? (
              <div className="card p-12 text-center">
                <FileText size={48} className="mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500">{t('dashboard.noReports')}</p>
                <p className="text-xs text-gray-400 mt-2">{t('dashboard.createViaHeaderPlus')}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myReports.map((report) => (
                  <div
                    key={report.id}
                    data-testid={`my-report-${report.id}`}
                    className="card p-6 cursor-pointer"
                    onClick={() => navigate(`/reports/${report.id}`)}
                  >
                    {Array.isArray(report?.photos) && report.photos.length > 0 && (
                      <img
                        src={report.photos[0]}
                        alt={report.title}
                        className="w-full h-40 object-cover rounded-lg mb-4"
                      />
                    )}
                    <div className="flex items-start justify-between mb-2 gap-3">
                      <h3 className="font-semibold text-gray-900 flex-1">{report.title}</h3>
                      <span className={`status-badge status-${report.status} text-xs`}>
                        {statusLabel(report.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{report.description}</p>
                    <div className="text-xs text-gray-500">{formatDate(report.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="donations" className="mt-8">
            {loading ? (
              <div className="text-center py-12">{t('common.loading')}</div>
            ) : myDonations.length === 0 ? (
              <div className="card p-12 text-center">
                <Heart size={48} className="mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500 mb-4">{t('dashboard.noDonations')}</p>
                <Button data-testid="browse-reports-btn" onClick={() => navigate('/reports')}>{t('dashboard.browseReports')}
                </Button>
              </div>
            ) : (
              <div className="card">
                <div className="divide-y">
                  {myDonations.map((donation, idx) => (
                    <div
                      key={donation.id ?? idx}
                      data-testid={`my-donation-${idx}`}
                      className="p-6 hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/reports/${donation.report_id}`)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-2xl font-bold text-cyan-600">₹{Number(donation.amount || 0).toLocaleString()}</span>
                            <span className={`status-badge ${donation.status === 'completed' ? 'status-completed' : 'status-submitted'} text-xs`}>
                              {statusLabel(donation.status)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">Report ID: {donation.report_id}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(donation.created_at)}</p>
                        </div>
                        {donation.status === 'completed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => downloadInvoice(donation.id, e)}
                            className="ml-4"
                            data-testid={`download-invoice-${donation.id}`}
                          >
                            <Download size={16} className="mr-2" />
                            Invoice
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default UserDashboard;